import { Link } from "react-router-dom";
import { ArrowRight, Users } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeIn, GoldButton } from "@/components/Reveal";
import { GROUPS } from "@/data/groups";
import { PACKAGES, PACKAGES_INTRO } from "@/data/island";

export default function PackagesPage() {
  return (
    <>
      <PageHero eyebrow="Group Packages" title="There is something for everyone" image="/images/packages-banner.jpg">
        Allow us to create a fun, memorable day for your group &mdash; tailored exactly to you.
      </PageHero>

      <section className="bg-navy-800 py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Our Packages</Eyebrow>
            {PACKAGES_INTRO.map((p, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <p className={`text-sm font-light leading-relaxed text-mist sm:text-base ${i ? "mt-5" : "mt-8"}`}>
                  {p}
                </p>
              </FadeIn>
            ))}
          </div>

          <div className="mt-16 grid gap-6 md:grid-cols-2">
            {PACKAGES.map((pkg, i) => {
              const group = GROUPS.find((g) => g.slug === pkg.slug);
              return (
                <FadeIn key={pkg.slug} delay={i * 0.08} className="h-full">
                  <div
                    data-testid={`package-card-${i + 1}`}
                    className="group relative flex h-full min-h-[340px] flex-col overflow-hidden border border-white/5"
                  >
                    {group && (
                      <img
                        src={group.heroImage}
                        alt={pkg.name}
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/55 to-navy-950/15" />
                    <div className="relative mt-auto flex flex-col p-8">
                      <h2 className="font-serif text-3xl text-ivory sm:text-4xl">{pkg.name}</h2>
                      <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-ivory/75">
                        {pkg.blurb}
                      </p>
                      <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/10 pt-5">
                        <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-ivory/70">
                          <Users className="h-3.5 w-3.5 text-gold" /> {pkg.capacity} guests
                        </span>
                        <span className="font-serif text-2xl text-gold">{pkg.price}</span>
                        <Link
                          to={`/groups/${pkg.slug}`}
                          data-testid={`package-details-${pkg.slug}`}
                          className="ml-auto inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gold"
                        >
                          View Details
                          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>

          <FadeIn delay={0.2}>
            <div className="mt-16 flex flex-col items-center justify-between gap-8 border border-gold/25 bg-navy-900/70 p-10 text-center sm:flex-row sm:text-left">
              <div>
                <h3 className="font-serif text-3xl text-ivory">
                  Reserve your stay with a <em className="italic text-gold">bespoke package</em>
                </h3>
                <p className="mt-3 text-sm font-light text-mist">
                  Call +353 86 251 8252 or send an enquiry &mdash; we'll build the perfect day.
                </p>
              </div>
              <GoldButton href="/contact" testId="packages-page-cta">
                Enquire Now
              </GoldButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
